import { useEffect, useState, useCallback } from "react";
import { getPhoto, PHOTO_EVENT } from "@/lib/patientPhoto";

/**
 * The single, reusable patient-avatar component.
 *
 * Renders either the uploaded photo (from `lib/patientPhoto`) or a warm,
 * name-hashed initials tile. Subscribes to the app-wide photo-changed event
 * bus so that when a photo is uploaded or removed anywhere in the app, every
 * mounted <PatientAvatar/> for that patient updates instantly — no page
 * refresh required.
 *
 * Use this component everywhere a patient avatar appears (list, profile
 * header, calendar cards, waiting room, chairside mode, billing, treatments,
 * appointment popups, search results). Never re-implement initial-rendering
 * inline — the whole point of this component is to keep visuals consistent.
 */

// A small, warm palette that echoes the doctor-color palette used on the
// calendar without clashing with row backgrounds.
const AVATAR_PALETTE = [
  { bg: "#E7EEE9", fg: "#1A362B" },  // pine
  { bg: "#FDF3ED", fg: "#8A3F1E" },  // clay
  { bg: "#F0E7EE", fg: "#4A2148" },  // plum
  { bg: "#F5EFE0", fg: "#7A5A20" },  // sand
  { bg: "#E4EFF5", fg: "#1F4B66" },  // sky
  { bg: "#F6E3E5", fg: "#7A2A2E" },  // rose
  { bg: "#E0EBDE", fg: "#264029" },  // forest
  { bg: "#E4E7E6", fg: "#212B27" },  // coal
];

const paletteFor = (seed = "") => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};

export const initialsFromName = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

// Preset sizes map to a numeric pixel value AND the storage variant to load.
const SIZE_PRESETS = {
  xs: { px: 24, variant: "thumb",  text: "text-[10px]" },
  sm: { px: 32, variant: "thumb",  text: "text-[11px]" },
  md: { px: 40, variant: "thumb",  text: "text-xs" },
  lg: { px: 64, variant: "medium", text: "text-xl" },
  xl: { px: 96, variant: "medium", text: "text-3xl" },
};

export default function PatientAvatar({
  patient,
  size = "md",
  clickable = false,
  onClick,
  className = "",
  ringClassName = "",
  title,
}) {
  const preset = typeof size === "string" ? (SIZE_PRESETS[size] || SIZE_PRESETS.md) : {
    px: size, variant: size >= 64 ? "medium" : "thumb", text: size >= 64 ? "text-xl" : "text-xs",
  };
  const pid = patient?.id;
  const name = patient?.name || "";
  const initials = initialsFromName(name);
  const palette = paletteFor(pid || name);

  const [src, setSrc] = useState(() => (pid ? getPhoto(pid, preset.variant) : null));
  const [failed, setFailed] = useState(false);

  // Re-read the photo whenever it changes for this patient.
  const refresh = useCallback(() => {
    if (!pid) return;
    setSrc(getPhoto(pid, preset.variant));
    setFailed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid, preset.variant]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onChanged = (e) => {
      // If the event is patient-specific, only refresh matching avatars;
      // otherwise refresh all (bulk imports, sign-out reset, etc.).
      if (!e?.detail?.patientId || e.detail.patientId === pid) refresh();
    };
    window.addEventListener(PHOTO_EVENT, onChanged);
    return () => window.removeEventListener(PHOTO_EVENT, onChanged);
  }, [pid, refresh]);

  const style = { width: preset.px, height: preset.px, background: palette.bg, color: palette.fg };
  const cursor = clickable ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5" : "";
  const base = `rounded-full grid place-items-center font-display shrink-0 overflow-hidden transition-all duration-150 ${preset.text} ${cursor} ${ringClassName} ${className}`;

  const inner = (src && !failed) ? (
    <img
      src={src}
      alt={name || "Patient"}
      draggable={false}
      className="w-full h-full object-cover"
      onError={() => setFailed(true)}
    />
  ) : (
    <span aria-hidden>{initials}</span>
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title || name}
        aria-label={name ? `Open ${name}'s profile` : "Open patient profile"}
        className={base}
        style={style}
        data-testid={pid ? `patient-avatar-${pid}` : undefined}
      >
        {inner}
      </button>
    );
  }

  return (
    <div
      title={title || name}
      className={base}
      style={style}
      data-testid={pid ? `patient-avatar-${pid}` : undefined}
    >
      {inner}
    </div>
  );
}
